import { supabase } from "@/integrations/supabase/client";
import type { AdCampaign, AdTriggerEvent } from "@/types";

let cache: { at: number; data: AdCampaign[] } | null = null;
const TTL = 60_000;

const FALLBACK: AdCampaign[] = [
  {
    id: "fb-coke",
    tenant_id: "univision-demo",
    name: "Refreshing Moment",
    sponsor_name: "Coca-Cola",
    logo_url: "https://upload.wikimedia.org/wikipedia/commons/c/ce/Coca-Cola_logo.svg",
    tagline: "Momento Refrescante",
    landing_url: "https://www.coca-cola.com",
    trigger_events: ["corner_kick", "halftime"],
    start_at: new Date().toISOString(),
    end_at: null,
    is_active: true,
    priority: 10,
    estimated_cpm: 32.5,
    estimated_cpa: 2.4,
    created_at: new Date().toISOString(),
  },
  {
    id: "fb-gatorade",
    tenant_id: "univision-demo",
    name: "Champion Energy",
    sponsor_name: "Gatorade",
    logo_url: "https://upload.wikimedia.org/wikipedia/commons/3/35/Gatorade_logo.svg",
    tagline: "Energía de Campeones",
    landing_url: "https://www.gatorade.com",
    trigger_events: ["high_speed_run", "dangerous_attack"],
    start_at: new Date().toISOString(),
    end_at: null,
    is_active: true,
    priority: 9,
    estimated_cpm: 28,
    estimated_cpa: 2.1,
    created_at: new Date().toISOString(),
  },
  {
    id: "fb-visa",
    tenant_id: "univision-demo",
    name: "Goal Celebration",
    sponsor_name: "Visa",
    logo_url: "https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg",
    tagline: "Celebra cada gol",
    landing_url: "https://www.visa.com",
    trigger_events: ["goal", "fulltime"],
    start_at: new Date().toISOString(),
    end_at: null,
    is_active: true,
    priority: 8,
    estimated_cpm: 35,
    estimated_cpa: 3.0,
    created_at: new Date().toISOString(),
  },
  {
    id: "fb-doritos",
    tenant_id: "univision-demo",
    name: "Halftime Snacks",
    sponsor_name: "Doritos",
    logo_url: "https://upload.wikimedia.org/wikipedia/commons/9/9b/Doritos_logo.svg",
    tagline: "¿Qué estás comiendo?",
    landing_url: "https://www.doritos.com",
    trigger_events: ["halftime"],
    start_at: new Date().toISOString(),
    end_at: null,
    is_active: true,
    priority: 7,
    estimated_cpm: 24,
    estimated_cpa: 1.8,
    created_at: new Date().toISOString(),
  },
];

export async function loadActiveCampaigns(force = false): Promise<AdCampaign[]> {
  const now = Date.now();
  if (!force && cache && now - cache.at < TTL) return cache.data;
  try {
    const { data, error } = await supabase
      .from("ad_campaigns")
      .select("*")
      .eq("is_active", true)
      .order("priority", { ascending: false });
    if (error || !data || data.length === 0) {
      cache = { at: now, data: FALLBACK };
      return FALLBACK;
    }
    const rows = data as unknown as AdCampaign[];
    cache = { at: now, data: rows };
    return rows;
  } catch {
    cache = { at: now, data: FALLBACK };
    return FALLBACK;
  }
}

export function pickCampaignForEvent(
  campaigns: AdCampaign[],
  event: AdTriggerEvent,
): AdCampaign | null {
  const now = Date.now();
  const eligible = campaigns
    .filter((c) => c.is_active && c.trigger_events.includes(event))
    .filter((c) => {
      const s = new Date(c.start_at).getTime();
      const e = c.end_at ? new Date(c.end_at).getTime() : Infinity;
      return now >= s && now <= e;
    })
    .sort((a, b) => b.priority - a.priority);
  return eligible[0] ?? null;
}

export function isSafeUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
}

export async function logImpression(args: {
  campaign_id: string;
  session_id?: string;
  event_type: string;
  clicked?: boolean;
}) {
  try {
    await supabase.from("ad_impressions").insert({
      campaign_id: args.campaign_id.startsWith("fb-") ? null : args.campaign_id,
      session_id: args.session_id ?? null,
      event_type: args.event_type,
      clicked: args.clicked ?? false,
    });
  } catch (e) {
    console.warn("[adEngine] impression log failed", e);
  }
}